import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, CheckIcon, FileTextIcon, SettingsIcon, SearchIcon, ChevronDownIcon, ChevronUpIcon, EuroIcon, LayersIcon, ShieldCheckIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function KostenWebsiteAccountantPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Kosten website accountant | Prijzen 2026 | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Wat kost een website voor een accountant? Prijzen vanaf €1.500 voor een professionele accountantswebsite. Inclusief klantenportaal, SEO en meer.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Kosten website accountant | Prijzen 2026 | Noord',
      'og:description': 'Wat kost een website voor een accountant? Prijzen vanaf €1.500 voor een professionele accountantswebsite.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/cluster/kosten-website-accountant',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const prijsfactoren = [{
    icon: FileTextIcon,
    title: "Aantal pagina's",
    description: "Een eenvoudige website met een homepagina, dienstenpagina en contactpagina is goedkoper dan een uitgebreide site met aparte pagina's voor elke dienst, een blog en een kennisbank."
  }, {
    icon: SettingsIcon,
    title: 'Functionaliteiten',
    description: 'Heeft jouw accountantskantoor een klantenportaal, afsprakenplanner of koppeling met boekhoudpakketten nodig? Elke extra functionaliteit beïnvloedt de prijs.'
  }, {
    icon: SearchIcon,
    title: 'SEO',
    description: 'Basis SEO is standaard inbegrepen. Wil je gericht gevonden worden op zoektermen als "accountant Alkmaar" of "boekhouder in de buurt"? Dan is uitgebreide SEO een slimme investering.'
  }];
  const kostenVoorbeelden = [{
    titel: 'Starter website',
    prijs: '€500 – €1.200',
    beschrijving: 'Een compacte website voor een startende accountant of eenmanszaak.',
    kenmerken: ['Homepagina + dienstenpagina + contact', 'Responsive design', 'Contactformulier', 'Basis SEO-optimalisatie']
  }, {
    titel: 'Professionele bedrijfswebsite',
    prijs: '€1.500 – €3.500',
    beschrijving: 'De meest gekozen optie voor gevestigde accountantskantoren.',
    kenmerken: ["5-10 pagina's met alle diensten", 'WordPress CMS voor zelf beheren', 'SEO-optimalisatie voor lokale vindbaarheid', 'Blog voor kennisdeling', 'Google Analytics integratie'],
    populair: true
  }, {
    titel: 'Maatwerk website',
    prijs: '€3.000 – €6.000+',
    beschrijving: 'Voor grotere kantoren met specifieke wensen en integraties.',
    kenmerken: ["Onbeperkt pagina's", 'Klantenportaal of afsprakenplanner', 'Koppeling met boekhoudpakketten', 'Geavanceerde SEO-strategie', 'Doorlopend onderhoud en support']
  }];
  const faqs = [{
    q: 'Wat kost een website voor een accountant gemiddeld?',
    a: "Een professionele website voor een accountantskantoor kost gemiddeld tussen de €1.500 en €3.500. Dit is afhankelijk van het aantal pagina's, de gewenste functionaliteiten en de mate van SEO-optimalisatie. Een eenvoudige starterswebsite kan al vanaf €500."
  }, {
    q: 'Zijn er maandelijkse kosten na oplevering?',
    a: 'Ja, naast de eenmalige bouwkosten zijn er maandelijkse kosten voor hosting en onderhoud. Bij Noord Digital bieden wij transparante onderhoudspakketten zodat jouw website altijd snel, veilig en up-to-date blijft.'
  }, {
    q: 'Kan ik zelf de website aanpassen?',
    a: 'Ja, wij bouwen op WordPress waardoor je eenvoudig zelf teksten, afbeeldingen en blogartikelen kunt aanpassen. Na oplevering geven wij een korte instructie zodat je direct aan de slag kunt.'
  }, {
    q: 'Is SEO inbegrepen bij de website?',
    a: 'Basis SEO is altijd inbegrepen: snelle laadtijden, juiste meta-tags en een goede sitestructuur. Wil je gericht gevonden worden op zoektermen als "accountant Alkmaar"? Dan adviseren wij een uitgebreid SEO-pakket.'
  }, {
    q: 'Hoe lang duurt het om een website voor een accountant te bouwen?',
    a: 'Een standaard bedrijfswebsite voor een accountantskantoor is gemiddeld binnen 2 tot 4 weken live. Dit hangt af van de omvang en hoe snel wij de benodigde content ontvangen.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1280">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1281">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1282">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1283" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1284" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1285" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1286">
          <div className="max-w-3xl" data-id="element-1287">
            <div className="flex items-center gap-2 mb-4 animate-fade-in-up" data-id="element-1288">
              <a href="#/sector/zakelijke-dienstverlening" className="text-accent-light hover:text-white transition-colors font-semibold text-sm tracking-widest uppercase" data-id="element-1289">
                Zakelijke Dienstverlening
              </a>
              <span className="text-navy-400 text-sm" data-id="element-1290">/</span>
              <a href="#/sector/zakelijke-dienstverlening/accountant" className="text-accent-light hover:text-white transition-colors font-semibold text-sm tracking-widest uppercase" data-id="element-1291">
                Accountant
              </a>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up-delay-1" data-id="element-1292">
              Wat kost een website voor een accountant?
            </h1>
            <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mb-8 animate-fade-in-up-delay-2" data-id="element-1293">
              Een overzicht van prijzen en opties voor een professionele website
              voor accountants en accountantskantoren.
            </p>
            <div className="animate-fade-in-up-delay-3" data-id="element-1294">
              <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1295">
                Vraag een prijsindicatie aan
                <ArrowRightIcon className="w-5 h-5" data-id="element-1296" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Gemiddelde kosten */}
      <section className="py-24 bg-surface-muted" data-id="element-1297">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1298">
          <div className="text-center mb-16" data-id="element-1299">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1300">
              Gemiddelde kosten voor een website voor accountants
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1301">
              De prijs van een website voor een accountantskantoor hangt af van
              de omvang en functionaliteiten. Hieronder een overzicht.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1302">
            {kostenVoorbeelden.map(voorbeeld => <div key={voorbeeld.titel} className={`bg-white rounded-card shadow-card p-8 flex flex-col relative ${voorbeeld.populair ? 'border-2 border-accent md:-translate-y-2' : ''}`} data-id="element-1303">
                {voorbeeld.populair && <div className="absolute -top-3 right-6 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-1304">
                    Meest gekozen
                  </div>}
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1305">
                  {voorbeeld.titel}
                </h3>
                <div className="text-3xl font-extrabold text-accent mb-4" data-id="element-1306">
                  {voorbeeld.prijs}
                </div>
                <p className="text-slate-600 mb-6 flex-grow" data-id="element-1307">
                  {voorbeeld.beschrijving}
                </p>
                <ul className="space-y-3" data-id="element-1308">
                  {voorbeeld.kenmerken.map(kenmerk => <li key={kenmerk} className="flex items-start gap-3" data-id="element-1309">
                      <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1310" />
                      <span className="text-slate-700 text-sm" data-id="element-1311">{kenmerk}</span>
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      {/* Wat bepaalt de prijs */}
      <section className="py-24 bg-white" data-id="element-1312">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1313">
          <div className="text-center mb-16" data-id="element-1314">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1315">
              Wat bepaalt de prijs van een website voor een accountant?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1316">
              Drie factoren die de kosten van jouw accountantswebsite
              beïnvloeden.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1317">
            {prijsfactoren.map(factor => <div key={factor.title} className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-1318">
                <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1319">
                  <factor.icon className="w-6 h-6 text-accent" data-id="element-1320" />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1321">
                  {factor.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-1322">
                  {factor.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Kosten voorbeelden — uitgebreid */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-1323">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-1324" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1325">
          <div className="text-center mb-16" data-id="element-1326">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1327">
              Waar betaal je voor bij een accountantswebsite?
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-1328">
              Een overzicht van de onderdelen die in de prijs zijn opgenomen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1329">
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1330">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center mb-4" data-id="element-1331">
                <EuroIcon className="w-5 h-5 text-accent-light" data-id="element-1332" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1333">
                Design op maat
              </h3>
              <p className="text-navy-300 text-sm leading-relaxed" data-id="element-1334">
                Een professioneel ontwerp dat past bij de uitstraling van jouw
                accountantskantoor en vertrouwen wekt bij potentiële klanten.
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1335">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center mb-4" data-id="element-1336">
                <LayersIcon className="w-5 h-5 text-accent-light" data-id="element-1337" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1338">
                Technische bouw
              </h3>
              <p className="text-navy-300 text-sm leading-relaxed" data-id="element-1339">
                Een snelle, veilige en responsive website gebouwd in WordPress,
                zodat je zelf eenvoudig content kunt aanpassen.
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1340">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center mb-4" data-id="element-1341">
                <SearchIcon className="w-5 h-5 text-accent-light" data-id="element-1342" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1343">
                SEO-optimalisatie
              </h3>
              <p className="text-navy-300 text-sm leading-relaxed" data-id="element-1344">
                Technische SEO, meta-tags en gestructureerde content zodat jouw
                kantoor gevonden wordt op relevante zoektermen.
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1345">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center mb-4" data-id="element-1346">
                <ShieldCheckIcon className="w-5 h-5 text-accent-light" data-id="element-1347" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1348">
                Hosting & onderhoud
              </h3>
              <p className="text-navy-300 text-sm leading-relaxed" data-id="element-1349">
                Betrouwbare hosting, SSL-certificaat, backups en technisch
                onderhoud zodat jouw website altijd online en veilig is.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-1350">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-semibold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-base group" data-id="element-1351">
              Vraag een vrijblijvende offerte aan
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" data-id="element-1352" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-1353">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1354">
          <div className="text-center mb-16" data-id="element-1355">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1356">
              Veelgestelde vragen over kosten website accountant
            </h2>
          </div>

          <div className="space-y-4" data-id="element-1357">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl overflow-hidden shadow-soft" data-id="element-1358">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-1359">
                  <span className="font-bold text-navy-900 pr-4" data-id="element-1360">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1361" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-1362" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-1363">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-1364">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1365">
          <h3 className="text-lg font-semibold text-navy-900 text-center mb-8" data-id="element-1366">
            Gerelateerde pagina's
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto" data-id="element-1367">
            <a href="#/sector/zakelijke-dienstverlening/accountant" className="flex items-center justify-between bg-surface-muted p-5 rounded-card border border-navy-100 hover:shadow-medium transition-all group" data-id="element-1368">
              <span className="font-medium text-navy-900" data-id="element-1369">
                Website accountant
              </span>
              <ArrowRightIcon className="w-4 h-4 text-accent group-hover:translate-x-1 transition-transform" data-id="element-1370" />
            </a>
            <a href="#/website-laten-maken" className="flex items-center justify-between bg-surface-muted p-5 rounded-card border border-navy-100 hover:shadow-medium transition-all group" data-id="element-1371">
              <span className="font-medium text-navy-900" data-id="element-1372">
                Website laten maken
              </span>
              <ArrowRightIcon className="w-4 h-4 text-accent group-hover:translate-x-1 transition-transform" data-id="element-1373" />
            </a>
            <a href="#/kosten-website-laten-maken" className="flex items-center justify-between bg-surface-muted p-5 rounded-card border border-navy-100 hover:shadow-medium transition-all group" data-id="element-1374">
              <span className="font-medium text-navy-900" data-id="element-1375">
                Kosten website laten maken
              </span>
              <ArrowRightIcon className="w-4 h-4 text-accent group-hover:translate-x-1 transition-transform" data-id="element-1376" />
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-1377" />
    </main>;
}